/** @format */

import {useState} from 'react';

import {styled} from '@mui/material/styles';

import Alert from '@mui/material/Alert';
import {Box, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import ModalLoading from 'components/loading/ModalLoading';
import ModalPage from 'components/ModalPage';
import {useSearchParams} from 'helpers/routerHelpers';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import {OauthProvider} from 'graphql/generated/types';

import type {account} from './Form';
import Form from './Form';
import {useGetCurrentUserQuery, useUpdateProviderTokenMutation} from '../graphql/__generated__';

const PREFIX = 'Subaccounts';

const classes = {
  title: `${PREFIX}-title`,
};

const Root = styled(Box)(({theme}) => ({
  [`& .${classes.title}`]: {
    color: theme.palette.text.secondary,
    fontSize: 20,
    fontWeight: 400,
    lineHeight: '23px',
  },
}));

export function Subaccounts() {
  const navigate = useNavigate();

  const searchParams = useSearchParams();
  const companyId = searchParams.get('companyId');
  const provider = searchParams.get('provider') as OauthProvider;

  const {mixpanel} = useMixpanelContext();

  const {t} = useTranslation();

  const [saveProviderTokenError, setSaveProviderTokenError] = useState('');

  const {loading, error, data} = useGetCurrentUserQuery({
    skip: !companyId,
    variables: {companyId: companyId!, provider},
  });

  const [updateProviderToken, {loading: updateProviderTokenLoading}] =
    useUpdateProviderTokenMutation();

  const renderHeader = () => {
    return (
      <Root>
        <Typography className={classes.title}>
          <Trans>Выберите один или несколько клиентских аккаунтов</Trans>
        </Typography>
      </Root>
    );
  };

  if (error) {
    const errorMessage = () => {
      const TOKEN_ERROR_PATTERNS = [
        {pattern: 'invalid_yandex_token', event: 'invalid_yandex_token'},
        {pattern: 'invalid oauth_token', event: 'invalid_oauth_token'},
      ];

      const lowerMessage = error.message.toLowerCase();
      const matched = TOKEN_ERROR_PATTERNS.find(({pattern}) => lowerMessage.includes(pattern));

      if (matched) {
        mixpanel.track(matched.event, {});
        const handleClick = () => {
          mixpanel.track('open_instruction_reconnect_token', {});
        };

        return (
          <Alert severity='error'>
            Проблема с доступом к кабинету. Пожалуйста переподключите доступ.
            <br />
            <a
              href='https://adsensor.ru/instructions#rec527787650'
              target='_blank'
              rel='noopener noreferrer'
              onClick={handleClick}
            >
              Инструкция
            </a>
          </Alert>
        );
      }
      return <Alert severity='error'>{error.message}</Alert>;
    };

    return <ModalPage header={renderHeader()}>{errorMessage()}</ModalPage>;
  }

  if (saveProviderTokenError) {
    return <Alert severity='error'>{saveProviderTokenError}</Alert>;
  }
  if (loading || !data?.currentUser) {
    return <ModalLoading title={t('Пожалуйста, подождите', 'Пожалуйста, подождите')} />;
  }

  const {company, restrictions, subscription} = data.currentUser;
  const token = company?.token;

  const handleSave = async (accounts: account[]) => {
    try {
      if (token?.id) {
        mixpanel.track('click_wizard_accounts_continue_button', {provider});

        await updateProviderToken({
          variables: {id: token.id, provider, accounts},
        });

        navigate(-1);
      }
    } catch (e) {
      setSaveProviderTokenError((e as Error).message);
    }
  };

  return (
    <Root>
      <ModalPage header={renderHeader()}>
        <Form
          maxSubaccountsCount={
            restrictions.subaccountsCount === undefined ? null : restrictions.subaccountsCount
          }
          companyAccounts={company?.accounts ?? []}
          tokenAccounts={token?.accounts ?? []}
          disableButtons={updateProviderTokenLoading}
          isTrial={subscription.isTrial}
          onSave={handleSave}
        />
      </ModalPage>
    </Root>
  );
}
